import React from 'react';
import cl from 'classnames';

import { IconsEnum } from '../../../../../../../assets/icons/types';
import { Icon } from '../../../../../../../helpers/Icon';
import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { PureTooltipIconButtonHelper } from '../../../../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { StaticImageHelper } from '../../../../../../../helpers/StaticImageHelper';
import { Translate } from '../../../../../../../helpers/Translate';

import { modelViewersKeys, words } from '../../../../../../../locales/keys';
import { TooltipPlacement } from '../../../../../../../helpers/tooltips/tooltipsConstants';

import { LifestyleGenerationItem } from '../../hooks/useModelViewerLifestyleGeneration';
import { RenderGenerationItem } from '../../hooks/useModelViewerRenderGeneration';
import {
  LifestyleGenerationMode,
  LifestyleGenerationStatus
} from '../../../../../../lifestyleGenerations/lifestyleGenerationsTypes';
import { RenderGenerationStatus } from '../../../../../../renderGenerations/renderGenerationsTypes';
import { buildDownloadName } from '../../utils/buildDownloadName';
import { downloadFile } from '../../../../../../../utils/downloadFile';

export type ModelViewerLifestyleSidebarGeneration =
  | LifestyleGenerationItem
  | RenderGenerationItem;

interface ModelViewerLifestyleSidebarProps {
  generations: ModelViewerLifestyleSidebarGeneration[];
  activeViewId: string;
  modelName?: string;
  onSelectView: (id: string) => void;
  onAdjust: (item: LifestyleGenerationItem) => void;
  onRemoveLifestyle: (localId: string) => void;
  onStopLifestyle: (localId: string) => void;
  onRemoveRender: (localId: string) => void;
  onStopRender: (localId: string) => void;
}

function ModelViewerLifestyleSidebar({
  generations,
  activeViewId,
  modelName,
  onSelectView,
  onAdjust,
  onRemoveLifestyle,
  onStopLifestyle,
  onRemoveRender,
  onStopRender
}: ModelViewerLifestyleSidebarProps) {
  return (
    <div className="flex flex-col md:border-l dark:border-gray-700 md:w-80 4xl:w-112 md:h-full md:rounded-br-3xl overflow-hidden">
      <div className="flex flex-col flex-1 overflow-hidden min-h-0">
        <div className="flex-1 px-2 py-2 overflow-y-auto space-y-3">
          {/* 3D model card */}
          <div
            onClick={() => onSelectView('model')}
            className={cl(
              'flex items-center gap-2.5 p-2 rounded-lg border cursor-pointer transition-colors',
              activeViewId === 'model'
                ? 'bg-gray-100 dark:bg-gray-800 border-purple-400 dark:border-purple-500'
                : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-750'
            )}
          >
            <div className="w-10 h-10 rounded bg-white dark:bg-gray-900 shrink-0 flex items-center justify-center border border-gray-200 dark:border-gray-700 overflow-hidden">
              <Icon
                className="h-6 w-6 text-gray-500"
                icon={IconsEnum.CUBE_TRANSPARENT_OUTLINE}
              />
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              <Translate id={modelViewersKeys.threeDModelViewer} />
            </span>
          </div>

          <hr className="border-gray-200 dark:border-gray-700 -mx-2" />
          <p className="text-2xs font-medium text-gray-500 dark:text-gray-400 px-1 pt-1 uppercase tracking-wide">
            <Translate id={modelViewersKeys.createdLifestyles} />
          </p>

          {/* Generation cards */}
          {generations.map((item) => {
            const isRenderGeneration = item.kind === 'render';
            const isActive = activeViewId === item.localId;
            const isLoading = isRenderGeneration
              ? item.status === RenderGenerationStatus.Pending ||
                item.status === RenderGenerationStatus.Processing
              : item.status === LifestyleGenerationStatus.Pending ||
                item.status === LifestyleGenerationStatus.Processing ||
                item.status === LifestyleGenerationStatus.PreparingSource;
            const isDone = isRenderGeneration
              ? item.status === RenderGenerationStatus.Completed
              : item.status === LifestyleGenerationStatus.Success;

            const modeLabelKey = isRenderGeneration
              ? modelViewersKeys.generationModeSiloRender
              : item.mode === LifestyleGenerationMode.Silo
              ? modelViewersKeys.generationModeSilo
              : item.mode === LifestyleGenerationMode.CloseUp
              ? modelViewersKeys.generationModeCloseUp
              : item.mode === LifestyleGenerationMode.Edit
              ? modelViewersKeys.generationModeEdit
              : item.selectedCommand
              ? modelViewersKeys.generationModeLifestyle
              : null;

            return (
              <div
                key={item.localId}
                onClick={() =>
                  (isLoading || isDone) && onSelectView(item.localId)
                }
                className={cl(
                  'rounded-xl border overflow-hidden bg-white dark:bg-gray-900 transition-colors',
                  {
                    'cursor-pointer': isLoading || isDone,
                    'border-blue-400 dark:border-blue-500':
                      (isActive || isLoading) && isRenderGeneration,
                    'border-purple-400 dark:border-purple-500':
                      (isActive || isLoading) && !isRenderGeneration,
                    'border-gray-200 dark:border-gray-700':
                      !isActive && !isLoading
                  }
                )}
              >
                {/* Image / placeholder */}
                {isLoading ? (
                  <div className="relative">
                    <div
                      className={cl(
                        'aspect-w-16 aspect-h-9 w-full',
                        isRenderGeneration
                          ? 'bg-cosmos-blue animate-gradient-angle'
                          : 'bg-gray-200 dark:bg-gray-700 animate-pulse'
                      )}
                    />
                    {modeLabelKey && (
                      <span className="absolute top-1.5 left-1.5 z-10 px-1.5 py-0.5 rounded text-2xs font-medium bg-black/50 text-white">
                        <Translate id={modeLabelKey} />
                      </span>
                    )}
                  </div>
                ) : isDone && item.resultPreviewUrl ? (
                  <div className="relative">
                    <StaticImageHelper
                      src={item.resultPreviewUrl}
                      alt=""
                      className="w-full h-auto block"
                    />
                    {modeLabelKey && (
                      <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-2xs font-medium bg-black/50 text-white">
                        <Translate id={modeLabelKey} />
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="aspect-w-16 aspect-h-9 w-full bg-red-100 dark:bg-red-900/20">
                    <span className="absolute inset-0 text-xs text-red-500 p-2 text-center flex items-center justify-center">
                      {item.errorMessage || (
                        <Translate id={modelViewersKeys.generationFailed} />
                      )}
                    </span>
                  </div>
                )}

                <div className="p-2 space-y-1.5">
                  {item.kind === 'lifestyle' &&
                    (item.prompt || !!item.referenceFileUrls?.length) && (
                      <div className="flex items-start gap-2">
                        {item.prompt && (
                          <p className="flex-1 text-2xs text-gray-500 dark:text-gray-400 line-clamp-3">
                            {item.prompt}
                          </p>
                        )}
                        {!!item.referenceFileUrls?.length && (
                          <div className="shrink-0 flex flex-wrap justify-end gap-1 max-w-[50%]">
                            {item.referenceFileUrls.map((url) => (
                              <StaticImageHelper
                                key={url}
                                src={url}
                                alt=""
                                className="shrink-0 w-8 h-8 rounded object-cover border dark:border-gray-700"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                  <div className="flex gap-1">
                    {/* Stop (while generating) — only button visible during load */}
                    {isLoading && (
                      <PureButtonHelper
                        i18nText={words.stop}
                        className="py-1.5 px-3 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 flex-1 justify-center transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isRenderGeneration) {
                            onStopRender(item.localId);
                          } else {
                            onStopLifestyle(item.localId);
                          }
                        }}
                      />
                    )}

                    {/* Download */}
                    {isDone && item.resultFullUrl && (
                      <PureIconButtonHelper
                        icon={IconsEnum.DOWNLOAD_SOLID}
                        iconClassName="h-5 w-5"
                        i18nText={words.download}
                        i18nTextClassName="block"
                        className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 flex-1 justify-center transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadFile({
                            downloadFile: {
                              file: item.resultFullUrl,
                              name: buildDownloadName(
                                modelName,
                                isRenderGeneration ? 'silo-render' : item.mode,
                                item.resultFullUrl
                              )
                            },
                            withBlob: true
                          });
                        }}
                      />
                    )}

                    {/* Adjust */}
                    {isDone && item.kind === 'lifestyle' && (
                      <PureTooltipIconButtonHelper
                        icon={IconsEnum.PENCIL_ALT_SOLID}
                        iconClassName="h-5 w-5"
                        tooltipI18nText={words.edit}
                        tooltipPlacement={TooltipPlacement.TOP}
                        className="py-1.5 px-1.5 rounded-md inline-flex items-center leading-5 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAdjust(item);
                        }}
                      />
                    )}

                    {/* Remove — hidden during generation */}
                    {!isLoading && (
                      <PureTooltipIconButtonHelper
                        icon={IconsEnum.TRASH_SOLID}
                        iconClassName="h-5 w-5"
                        tooltipI18nText={words.remove}
                        tooltipPlacement={TooltipPlacement.TOP}
                        className="py-1.5 px-1.5 rounded-md inline-flex items-center leading-5 focus:ring-base text-white bg-red-500 hover:bg-red-600 shadow-sm transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isRenderGeneration) {
                            onRemoveRender(item.localId);
                          } else {
                            onRemoveLifestyle(item.localId);
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ModelViewerLifestyleSidebar;
